import { useEffect, useRef } from "react";
import { log } from "../../utils/logger";

/**
 * Embed a YouTube video and force-pause at `endSeconds`.
 * Uses postMessage to talk to the YouTube IFrame Player API
 * because the `?end=` URL param is unreliable on its own.
 */
export default function ClippedYouTube({ videoId, endSeconds, title }) {
  const iframeRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    pausedRef.current = false;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const post = (func) => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func, args: "" }),
        "*"
      );
    };

    // Subscribe to player events
    const subscribe = () => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "listening", id: 1, channel: "widget" }),
        "*"
      );
    };

    // Poll currentTime every 500ms
    const interval = setInterval(() => {
      if (!pausedRef.current) post("getCurrentTime");
    }, 500);

    const onMessage = (event) => {
      if (typeof event.data !== "string") return;
      if (!event.origin.includes("youtube.com")) return;
      try {
        const data = JSON.parse(event.data);
        const t = data?.info?.currentTime ?? (typeof data?.info === "number" ? data.info : null);
        if (typeof t === "number" && t >= endSeconds && !pausedRef.current) {
          pausedRef.current = true;
          post("pauseVideo");
          post("seekTo");
        }
      } catch (err) {
        log.warn("Ignoring non-JSON player message", err);
      }
    };

    window.addEventListener("message", onMessage);
    // Subscribe once iframe finishes loading
    const onLoad = () => setTimeout(subscribe, 500);
    iframe.addEventListener("load", onLoad);
    setTimeout(subscribe, 1500); // Fallback in case load already fired

    return () => {
      clearInterval(interval);
      window.removeEventListener("message", onMessage);
      iframe.removeEventListener("load", onLoad);
    };
  }, [endSeconds, videoId]);

  return (
    <iframe
      ref={iframeRef}
      className="absolute inset-0 h-full w-full"
      src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&end=${endSeconds}&rel=0`}
      title={title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
}
